// Data hooks for shows (podcasts), episodes, their media and the RSS feed.
// Mutations invalidate exactly the queries whose data they change.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePodcastInput,
  UpdatePodcastInput,
  CreateEpisodeInput,
  UpdateEpisodeInput,
  EpisodeStatus,
  EpisodeType,
} from '@podcastwiz-app/shared';
import { api } from './api';
import { uploadFile } from './upload';

export interface Show {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  author: string | null;
  language: string;
  category: string | null;
  podcastType: 'episodic' | 'serial';
  explicit: boolean;
  artworkUrl: string | null;
  ownerEmail: string | null;
  episodeCount?: number;
  createdAt: string;
}

export interface ShowEpisode {
  id: string;
  podcastId: string;
  seasonId: string | null;
  title: string;
  slug: string;
  description: string | null;
  showNotes: string | null;
  episodeNumber: number | null;
  episodeType: EpisodeType;
  status: EpisodeStatus;
  durationMs: number | null;
  publishedAt: string | null;
  scheduledAt: string | null;
  mediaFileId: string | null;
  artworkUrl: string | null;
  createdAt: string;
}

export interface MediaFile {
  id: string;
  originalName: string | null;
  format: string;
  durationMs: number | null;
  sampleRate: number | null;
  channels: number | null;
  fileSizeBytes: number;
  loudnessLufs: number | null;
  peakDb: number | null;
  waveformJson: { peaks: number[] } | null;
  /** Mastered MP3 the feed publishes (null until mastering has run). */
  deliveryPath: string | null;
  deliveryLufs: number | null;
  /** Transcript edits: ranges cut from the original (original-timeline ms). */
  editCuts?: Array<{ startMs: number; endMs: number }> | null;
  /** Render of the original minus editCuts; null when unedited. */
  editedPath?: string | null;
  editedDurationMs?: number | null;
  storagePath: string;
  createdAt: string;
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

// ── Shows ──

export function useShows() {
  return useQuery<{ podcasts: Show[]; total: number }>({
    queryKey: ['podcasts'],
    queryFn: () => api('/api/podcasts?limit=100'),
  });
}

export function useShow(id: string | undefined) {
  return useQuery<Show>({
    queryKey: ['podcast', id],
    queryFn: async () => (await api<{ podcast: Show }>(`/api/podcasts/${id}`)).podcast,
    enabled: !!id,
  });
}

export function useCreateShow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<CreatePodcastInput> & { title: string }) =>
      (await api<{ podcast: Show }>('/api/podcasts', { method: 'POST', ...json(input) })).podcast,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['podcasts'] }),
  });
}

export function useUpdateShow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdatePodcastInput) =>
      (await api<{ podcast: Show }>(`/api/podcasts/${id}`, { method: 'PATCH', ...json(input) })).podcast,
    onSuccess: (show) => {
      qc.setQueryData(['podcast', id], show);
      return qc.invalidateQueries({ queryKey: ['podcasts'] });
    },
  });
}

export function useDeleteShow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api(`/api/podcasts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['podcast', id] });
      return qc.invalidateQueries({ queryKey: ['podcasts'] });
    },
  });
}

/** Upload cover art; the API stores it and sets the show's artworkUrl. */
export function useUploadArtwork(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) =>
      (await uploadFile<{ podcast: Show }>(`/api/podcasts/${id}/artwork`, file)).podcast,
    onSuccess: (show) => {
      qc.setQueryData(['podcast', id], show);
      return qc.invalidateQueries({ queryKey: ['podcasts'] });
    },
  });
}

export interface ArtworkCheck {
  ok: boolean;
  message: string;
}

/** Apple: square JPEG/PNG, 1400–3000 px, RGB. Checked before upload. */
export function checkArtworkSize(width: number, height: number, type: string): ArtworkCheck {
  if (!['image/jpeg', 'image/png'].includes(type)) return { ok: false, message: 'Use a JPEG or PNG image.' };
  if (width !== height) return { ok: false, message: `Cover art must be square (this is ${width}×${height}).` };
  if (width < 1400 || width > 3000) {
    return { ok: false, message: `Cover art must be 1400–3000 px (this is ${width}×${height}).` };
  }
  return { ok: true, message: `${width}×${height} — meets Apple’s requirements.` };
}

/** Decode an image file's dimensions in the browser. */
export function imageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('unreadable_image'));
    };
    img.src = url;
  });
}

// ── Episodes ──

export function useShowEpisodes(podcastId: string | undefined) {
  return useQuery<{ episodes: ShowEpisode[]; total: number }>({
    queryKey: ['episodes', podcastId],
    queryFn: () => api(`/api/episodes?podcastId=${encodeURIComponent(podcastId!)}&limit=100`),
    enabled: !!podcastId,
  });
}

export function useEpisode(id: string | undefined) {
  return useQuery<ShowEpisode>({
    queryKey: ['episode', id],
    queryFn: async () => (await api<{ episode: ShowEpisode }>(`/api/episodes/${id}`)).episode,
    enabled: !!id,
  });
}

export function useCreateEpisode(podcastId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<CreateEpisodeInput> & { title: string }) =>
      (await api<{ episode: ShowEpisode }>('/api/episodes', { method: 'POST', ...json({ ...input, podcastId }) }))
        .episode,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['episodes', podcastId] }),
  });
}

export function useUpdateEpisode(episode: Pick<ShowEpisode, 'id' | 'podcastId'>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateEpisodeInput) =>
      (await api<{ episode: ShowEpisode }>(`/api/episodes/${episode.id}`, { method: 'PATCH', ...json(input) }))
        .episode,
    onSuccess: (updated) => {
      qc.setQueryData(['episode', episode.id], updated);
      return qc.invalidateQueries({ queryKey: ['episodes', episode.podcastId] });
    },
  });
}

export function useDeleteEpisode(episode: Pick<ShowEpisode, 'id' | 'podcastId'>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api(`/api/episodes/${episode.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['episode', episode.id] });
      return qc.invalidateQueries({ queryKey: ['episodes', episode.podcastId] });
    },
  });
}

// ── Media ──

export function useMediaFile(id: string | null | undefined) {
  const qc = useQueryClient();
  return useQuery<MediaFile>({
    queryKey: ['media', id],
    queryFn: async () => (await api<{ mediaFile: MediaFile }>(`/api/media/${id}`)).mediaFile,
    enabled: !!id,
    // Analysis (waveform, loudness, duration) lands a few seconds after
    // upload or a re-analyse; poll until it has.
    // So does the mastered MP3, more slowly for long episodes.
    refetchInterval: (query) => {
      const requestedAt = qc.getQueryData<number>(['media-analysis', id]);
      if (isAnalysing(query.state.data, query.state.dataUpdateCount, requestedAt)) return ANALYSIS_POLL_MS;
      return isMastering(query.state.data, requestedAt) ? MASTERING_POLL_MS : false;
    },
  });
}

export const MASTERING_POLL_MS = 5000;
/** Mastering a long episode takes minutes; past this it isn't coming. */
export const MASTERING_WINDOW_MS = 15 * 60_000;

/**
 * The mastered MP3 is still on its way (as opposed to a file from before
 * mastering existed, or a failed job): none yet, and uploaded or
 * re-analysed recently.
 */
export function isMastering(media: MediaFile | undefined, requestedAt?: number, now: number = Date.now()): boolean {
  if (!media || media.deliveryPath) return false;
  const since = requestedAt ?? Date.parse(media.createdAt);
  return now - since <= MASTERING_WINDOW_MS;
}

export const ANALYSIS_POLL_MS = 3000;
export const ANALYSIS_MAX_POLLS = 40;
/** Analysis normally finishes in seconds; older unanalysed files never will. */
export const ANALYSIS_WINDOW_MS = 5 * 60_000;

/**
 * Still waiting on analysis (as opposed to never going to get it): no
 * duration yet, uploaded or re-analysed recently, and polls remain.
 */
export function isAnalysing(
  media: MediaFile | undefined,
  polls: number,
  requestedAt?: number,
  now: number = Date.now(),
): boolean {
  if (!media || media.durationMs !== null || polls >= ANALYSIS_MAX_POLLS) return false;
  const since = requestedAt ?? Date.parse(media.createdAt);
  return !(now - since > ANALYSIS_WINDOW_MS);
}

/** Re-run waveform/loudness/duration analysis, then poll again. */
export function useAnalyzeMedia(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api(`/api/media/${id}/analyze`, { method: 'POST' }),
    onSuccess: () => {
      qc.setQueryData(['media-analysis', id], Date.now());
      return qc.resetQueries({ queryKey: ['media', id] });
    },
  });
}

export function useUploadMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, onProgress }: { file: File; onProgress?: (fraction: number) => void }) =>
      (await uploadFile<{ mediaFile: MediaFile }>('/api/media/upload', file, onProgress)).mediaFile,
    onSuccess: (media) => {
      qc.setQueryData(['media', media.id], media);
      return qc.invalidateQueries({ queryKey: ['media-list'] });
    },
  });
}

// ── Feed ──

export function feedUrl(podcastId: string, origin: string = window.location.origin): string {
  return `${origin}/podcasts/${podcastId}/feed.xml`;
}

export function useRefreshFeed(podcastId: string) {
  return useMutation({
    mutationFn: () => api(`/api/podcasts/${podcastId}/rss-gen`, { method: 'POST' }),
  });
}

// ── Formatting ──

export function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms < 0) return '—';
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** Podcast loudness target (Apple / Spotify): −16 LUFS ±1 for stereo. */
export const LOUDNESS_TARGET = -16;

export function loudnessVerdict(lufs: number | null | undefined): { ok: boolean; label: string } | null {
  if (lufs === null || lufs === undefined) return null;
  const diff = lufs - LOUDNESS_TARGET;
  if (Math.abs(diff) <= 1) return { ok: true, label: `${lufs.toFixed(1)} LUFS — on target` };
  return {
    ok: false,
    label: `${lufs.toFixed(1)} LUFS — ${Math.abs(diff).toFixed(1)} dB ${diff > 0 ? 'too loud' : 'too quiet'}`,
  };
}

// ── Analytics ──

export interface ShowAnalytics {
  days: number;
  totals: { allTime: number; window: number };
  daily: Array<{ date: string; downloads: number }>;
  episodes: Array<{ episodeId: string; title: string; window: number; allTime: number }>;
  apps: Array<{ name: string; downloads: number }>;
}

export function useShowAnalytics(podcastId: string, days: number) {
  return useQuery<ShowAnalytics>({
    queryKey: ['analytics', podcastId, days],
    queryFn: () => api(`/api/podcasts/${podcastId}/analytics?days=${days}`),
  });
}

/** 1,284 / 12.9K / 1.2M */
export function compactNumber(n: number): string {
  return new Intl.NumberFormat('en', { notation: n >= 10_000 ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(n);
}

/** Clean axis maximum and ticks: 0, half, max, with max from 1/2/5 × 10^k. */
export function niceTicks(max: number): number[] {
  if (max <= 0) return [0, 1];
  const exp = 10 ** Math.floor(Math.log10(max));
  const top = [1, 2, 5, 10].map((m) => m * exp).find((v) => v >= max)!;
  return top % 2 === 0 ? [0, top / 2, top] : [0, top];
}
